import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { ICommunicationType, IJob, IJobCandidateInteraction, ILocation } from 'wisestep-api';
import { formattedDateISO, WsSelectObj } from 'wisestep-base';

import { workAuthorization } from '../../../components/filters/components/AdvanceFilterHelper';
import TalentPoolAdvanceFilterFormProps from '../../../components/filters/models/TalentPoolAdvanceFiltersFormProps';
import {
  contactAvailabilityFilterValues,
  getCountryByLocation,
  jobPreferenceFilterValues,
} from '../../../features/candidates/utils/CandidateHelper';
import { FiltersTagProp } from '../components/filterTagList/FiltersTagList';
import { formatLocation } from '../components/helper/JobsCrmHelper';

export interface IJobTalentPoolFormType extends Partial<TalentPoolAdvanceFilterFormProps> {
  sort: string;
  radiusInMiles: number;
  locations: ILocation[];
  searchScope: string;
  candidateLocationPref: string;
  query: string;
}

export const JobTalentPoolDefaultRequest = (): IJobCandidateInteraction => ({
  id: '',
  workspaceId: '',
  query: '',
  totalResults: '',
  timeTakenInMillis: 0,
  page: 0,
  size: 20,
  sort: ['createdOn,desc'],
  workspaceName: '',
  sources: [],
  tags: [],
  employers: [],
  currentEmployers: [],
  clientNames: [],
  candidateLinkedInURL: '',
  candidateProfileURLs: [],
  candidateLocations: [],
  candidateAllLocationFields: [],
  workAuthorizationCategories: [],
  candidateName: '',
  languages: [],
  salaryCurrency: '',
  minSalary: 0,
  maxSalary: 0,
  radiusInMiles: 0,
  searchScope: 'EVERYTHING',
  searchWithMostRecentConversation: true,
});

export const getJobTalentPoolDefaultPayload = (
  job: IJob,
  booleanString: string
): IJobCandidateInteraction => ({
  ...JobTalentPoolDefaultRequest(),
  jobIds: job?.id ?? '',
  jobCode: job?.jobCode,
  candidateLocations: getCountryByLocation(job?.locations ?? []),
  query: booleanString,
});

export const getFormDefaultFilters = (query: IJobCandidateInteraction): IJobTalentPoolFormType => ({
  createBWDates: {
    title: '',
    fromDateName: (query.fromDateTime as Date) ?? null,
    toDateName: (query.toDateTime as Date) ?? null,
  },
  candidateStatus: query.candidateSearchTypeList ?? [],
  contactAvailability: contactAvailabilityFilterValues(
    query.emailAvailability ?? false,
    query.phoneAvailability ?? false
  ),
  jobTitles: query.jobTitles ?? [],
  workAuthorization: query.workAuthorizationCategories ?? [],
  currentEmployers: query.currentEmployers ?? [],
  previousEmployers: query.employers ?? [],
  salaryRange: {
    currency: query.salaryCurrency ?? null,
    unitTime: query.salaryTimeSpan ?? null,
    max: query.maxSalary ?? 0,
    min: query.minSalary ?? 0,
  },
  noticePeriod: {
    min: query.noticePeriodMinDays ?? 0,
    max: query.noticePeriodMaxDays ?? 0,
  },
  jobType: query.jobType ?? null,
  languages: query.languages ?? [],
  jobPreference: jobPreferenceFilterValues(
    query.jobPreferenceContract ?? false,
    query.jobPreferencePermanent ?? false
  ),
  tags: query.tags ?? [],
  source: query.sources ?? [],
  sort: query?.sort?.[0] ?? '',
  radiusInMiles: query.radiusInMiles ?? 0,
  locations: (query.candidateLocations as ILocation[]) ?? [],
  totalYearsExperienceStartRange: query?.totalYearsExperienceStartRange ?? 0,
  totalYearsExperienceEndRange: query?.totalYearsExperienceEndRange ?? 0,
  experienceComparisonOperator: query?.experienceComparisonOperator ?? 'isBetween',
  searchScope: 'EVERYTHING',
  query: query.query ?? '',
  candidateLocationPref: 'Current or Preferred',
  commPreference: [],
});

export type JobTalentPoolDrawerType = 'Sms' | 'Email' | 'None';

export const getUpdateJobTalentPoolQuery = (
  data: IJobTalentPoolFormType,
  query: IJobCandidateInteraction
): IJobCandidateInteraction => {
  const updatedQuery = { ...query };
  [
    'emailAvailability',
    'phoneAvailability',
    'jobPreferenceContract',
    'jobPreferencePermanent',
    'commPreferencePhone',
    'commPreferenceEmail',
  ].forEach((field) => delete updatedQuery[field]);
  updatedQuery.page = 0;
  updatedQuery.jobTitles = data?.jobTitles ?? [];
  updatedQuery.jobType = data?.jobType;
  updatedQuery.sources = data?.source ?? [];
  updatedQuery.languages = data?.languages ?? [];
  updatedQuery.tags = data?.tags ?? [];
  updatedQuery.currentEmployers = data?.currentEmployers;
  updatedQuery.employers = data?.previousEmployers ?? [];
  updatedQuery.workAuthorizationCategories = [];
  data?.workAuthorization?.forEach((obj) =>
    updatedQuery.workAuthorizationCategories?.push(workAuthorization[obj]?.toLowerCase())
  );

  updatedQuery.minSalary = data?.salaryRange?.min ?? 0;
  updatedQuery.maxSalary = data?.salaryRange?.max ?? 0;
  updatedQuery.salaryCurrency = data?.salaryRange?.currency ?? null;
  updatedQuery.salaryTimeSpan = data?.salaryRange?.unitTime ?? null;
  updatedQuery.query = data?.query ?? '';
  updatedQuery.fromDateTime = data?.createBWDates?.fromDateName
    ? formattedDateISO(data?.createBWDates?.fromDateName)
    : '';
  updatedQuery.toDateTime = data.createBWDates?.toDateName
    ? formattedDateISO(data.createBWDates?.toDateName)
    : '';
  if (updatedQuery.fromDateTime != null && !isEmpty(updatedQuery.fromDateTime)) {
    updatedQuery.fromDateTime = new Date(updatedQuery.fromDateTime);
    updatedQuery.fromDateTime.setHours(0, 0, 0);
    updatedQuery.fromDateTime = formattedDateISO(updatedQuery.fromDateTime);
    updatedQuery.datesUnionBy = 'CREATED_BETWEEN';
  }
  if (updatedQuery.toDateTime != null && !isEmpty(updatedQuery.toDateTime)) {
    updatedQuery.toDateTime = new Date(updatedQuery.toDateTime);
    updatedQuery.toDateTime.setHours(23, 59, 59);
    updatedQuery.toDateTime = formattedDateISO(updatedQuery.toDateTime);
  }
  data?.jobPreference?.forEach((obj) => {
    if (obj == 'Contract') updatedQuery.jobPreferenceContract = true;
    else if (obj == 'Permanent') updatedQuery.jobPreferencePermanent = true;
  });
  data?.contactAvailability?.forEach((obj) => {
    if (obj == 'Email') updatedQuery.emailAvailability = true;
    else if (obj == 'Phone') updatedQuery.phoneAvailability = true;
    else if (obj == 'Both') {
      updatedQuery.phoneAvailability = true;
      updatedQuery.emailAvailability = true;
    }
  });
  updatedQuery.noticePeriodMaxDays = data?.noticePeriod?.max ?? 0;
  updatedQuery.noticePeriodMinDays = data?.noticePeriod?.min ?? 0;
  updatedQuery.languages = data?.languages ?? [];
  updatedQuery.candidateSearchTypeList = (data?.candidateStatus ?? []).map((obj) =>
    obj.toLowerCase()
  );
  updatedQuery.currentStageCurrentStatusPairs = data?.stageStatus;
  updatedQuery.tags = data?.tags ?? [];
  updatedQuery.experienceComparisonOperator = data?.experienceComparisonOperator ?? 'isBetween';
  updatedQuery.totalYearsExperienceStartRange = data?.totalYearsExperienceStartRange ?? 0;
  updatedQuery.totalYearsExperienceEndRange = data?.totalYearsExperienceEndRange ?? 0;
  updatedQuery.candidateAllLocationFields = data?.locations ?? [];
  updatedQuery.candidateLocations = data?.locations ?? [];
  updatedQuery.radiusInMiles = data?.radiusInMiles;
  data.commPreference?.forEach((obj) => {
    if (obj == 'Email') {
      updatedQuery.commPreferenceEmail = true;
      updatedQuery.commType = ICommunicationType.Email;
    } else if (obj == 'Phone') {
      updatedQuery.commPreferencePhone = true;
      updatedQuery.commType = ICommunicationType.Phone;
    } else if (obj == 'Both') {
      updatedQuery.commPreferencePhone = true;
      updatedQuery.commPreferenceEmail = true;
      updatedQuery.commType = ICommunicationType.All;
    }
  });
  if (data?.candidateLocationPref === 'Current') {
    updatedQuery.searchInCurrentLocation = true;
    updatedQuery.searchInPreferredLocation = false;
  } else if (data?.candidateLocationPref === 'Preferred') {
    updatedQuery.searchInCurrentLocation = false;
    updatedQuery.searchInPreferredLocation = true;
  } else {
    updatedQuery.searchInCurrentLocation = true;
    updatedQuery.searchInPreferredLocation = true;
  }
  if (data?.query !== undefined && data?.query.length > 0) {
    const srt = data?.sort ? data?.sort.split(',') : ['desc'];
    updatedQuery.sort = [`_score,${srt[1] ?? 'desc'}`];
  } else {
    updatedQuery.sort = [data?.sort ?? 'createdOn,desc'];
  }
  return updatedQuery;
};

export const formattedDate = (date: string): string => dayjs(date).format('DD/MM/YYYY');

export const getActiveFilters = (filters: IJobTalentPoolFormType): FiltersTagProp[] => {
  const results: FiltersTagProp[] = [];
  results.push({
    label: `Location Preference : ${filters.candidateLocationPref} `,
    disableRemove: true,
    id: 'candidateLocationPref#0',
  });
  if (filters?.locations?.length) {
    filters?.locations?.forEach((loc, index) =>
      results.push({
        label: `Location: ${formatLocation(loc)}`,
        id: `locations#${index}`,
      })
    );
  }
  if (filters?.radiusInMiles > 0) {
    results.push({
      label: `Radius : ${filters.radiusInMiles} Miles`,
      id: 'radiusInMiles#0',
    });
  }
  if (filters?.jobTitles?.length) {
    filters?.jobTitles?.forEach((title, index) =>
      results.push({
        label: `Job Title: ${title}`,
        id: `jobTitles#${index}`,
      })
    );
  }
  if (filters?.createBWDates?.fromDateName && filters?.createBWDates?.toDateName) {
    results.push({
      label: `Created Between : ${formattedDate(
        filters?.createBWDates?.fromDateName.toString()
      )} - ${formattedDate(filters?.createBWDates?.toDateName.toString())} `,
      id: 'createBWDates#0',
    });
  }
  if (filters?.workAuthorization?.length) {
    filters?.workAuthorization?.forEach((workAuth, index) =>
      results.push({
        label: `Work Authorization : ${workAuth}`,
        id: `workAuthorization#${index}`,
      })
    );
  }
  if (filters?.source?.length) {
    filters?.source?.forEach((source, index) =>
      results.push({
        label: `Source : ${source}`,
        id: `source#${index}`,
      })
    );
  }
  if (filters?.commPreference?.length) {
    filters?.commPreference?.forEach((commPreference, index) =>
      results.push({
        label: `Communication Preference : ${commPreference}`,
        id: `commPreference#${index}`,
      })
    );
  }
  if (filters?.currentEmployers?.length) {
    filters?.currentEmployers?.forEach((curEmp, index) =>
      results.push({
        label: `Current Employer : ${curEmp}`,
        id: `currentEmployers#${index}`,
      })
    );
  }
  if (filters?.previousEmployers?.length) {
    filters?.previousEmployers?.forEach((prevEmp, index) =>
      results.push({
        label: `Previous Employer : ${prevEmp}`,
        id: `previousEmployers#${index}`,
      })
    );
  }
  if (filters?.contactAvailability?.length) {
    filters?.contactAvailability?.forEach((contact, index) =>
      results.push({
        label: `Contact Availability : ${contact}`,
        id: `contactAvailability#${index}`,
      })
    );
  }
  if (
    filters?.salaryRange?.currency &&
    filters?.salaryRange?.max > 0 &&
    filters?.salaryRange?.min > 0 &&
    filters?.salaryRange?.unitTime
  ) {
    results.push({
      label: `Salary Range : ${filters?.salaryRange?.currency} ${filters?.salaryRange?.min} - ${filters?.salaryRange?.max} ${filters?.salaryRange?.unitTime}`,
      id: `salaryRange#0`,
    });
  }
  if (
    filters?.noticePeriod?.max &&
    filters?.noticePeriod?.max > 0 &&
    filters?.noticePeriod?.min > 0
  ) {
    results.push({
      label: `Notice Period : ${filters?.noticePeriod?.min} - ${filters?.noticePeriod?.max} Days`,
      id: `noticePeriod#0`,
    });
  }
  if (filters?.languages?.length) {
    filters?.languages?.forEach((language, index) =>
      results.push({
        label: `Language : ${language}`,
        id: `languages#${index}`,
      })
    );
  }
  if (filters?.jobType) {
    results.push({
      label: `Job Type : ${filters?.jobType} `,
      id: `jobType#0`,
    });
  }
  if (filters?.jobPreference?.length) {
    filters?.jobPreference?.forEach((preJob, index) =>
      results.push({
        label: `Job Preference : ${preJob}`,
        id: `jobPreference#${index}`,
      })
    );
  }
  if (filters?.totalYearsExperienceStartRange && filters?.totalYearsExperienceStartRange > 0) {
    switch (filters?.experienceComparisonOperator) {
      case 'isBetween':
        results.push({
          label: `Experience : ${filters?.totalYearsExperienceStartRange}-${filters?.totalYearsExperienceEndRange} Years`,
          id: `experienceComparisonOperator#0`,
        });
        break;
      case 'isEqual':
        results.push({
          label: `Experience : ${filters?.totalYearsExperienceStartRange} Years `,
          id: `experienceComparisonOperator#0`,
        });
        break;
      default:
        break;
    }
  }
  if (filters?.candidateStatus?.length) {
    filters?.candidateStatus?.forEach((status, index) =>
      results.push({
        label: `Candidate Status : ${status}`,
        id: `candidateStatus#${index}`,
      })
    );
  }
  if (filters?.tags?.length) {
    filters?.tags?.forEach((tag, index) =>
      results.push({
        label: `Tag : ${tag}`,
        id: `tags#${index}`,
      })
    );
  }
  return results;
};

export type JobTalentFilterKey = keyof IJobTalentPoolFormType;

export const getJobTalentPoolQueryAndFormValues = (
  removeTag: JobTalentFilterKey,
  query: IJobCandidateInteraction,
  activeFilters: IJobTalentPoolFormType,
  index: number
): { updatedQuery: IJobCandidateInteraction; updatedFormValues: IJobTalentPoolFormType } => {
  const updatedQuery = { ...query };
  const updatedFormValues = { ...activeFilters };

  switch (removeTag) {
    case 'candidateStatus':
      updatedFormValues.candidateStatus = [
        ...(activeFilters?.candidateStatus ?? []).slice(0, index),
        ...(activeFilters?.candidateStatus ?? []).slice(index + 1),
      ];
      updatedQuery.candidateSearchTypeList = (updatedFormValues?.candidateStatus ?? []).map((obj) =>
        obj.toLowerCase()
      );
      break;
    case 'contactAvailability':
      updatedFormValues.contactAvailability = [
        ...(activeFilters?.contactAvailability ?? []).slice(0, index),
        ...(activeFilters?.contactAvailability ?? []).slice(index + 1),
      ];
      updatedQuery.emailAvailability =
        updatedFormValues.contactAvailability.includes('Both') ||
        updatedFormValues.contactAvailability.includes('Email');
      updatedQuery.phoneAvailability =
        updatedFormValues.contactAvailability.includes('Both') ||
        updatedFormValues.contactAvailability.includes('Phone');
      break;
    case 'radiusInMiles':
      updatedFormValues.radiusInMiles = 0;
      updatedQuery.radiusInMiles = 0;
      break;
    case 'jobPreference':
      updatedFormValues.jobPreference = [
        ...(activeFilters?.jobPreference ?? []).slice(0, index),
        ...(activeFilters?.jobPreference ?? []).slice(index + 1),
      ];
      updatedQuery.jobPreferenceContract = updatedFormValues.jobPreference?.includes('Contract');
      updatedQuery.jobPreferencePermanent = updatedFormValues.jobPreference?.includes('Permanent');
      break;
    case 'source':
      updatedFormValues.source = [
        ...(activeFilters?.source ?? []).slice(0, index),
        ...(activeFilters?.source ?? []).slice(index + 1),
      ];
      updatedQuery.sources = updatedFormValues.source;
      break;
    case 'jobTitles':
      updatedFormValues.jobTitles = [
        ...(activeFilters?.jobTitles ?? []).slice(0, index),
        ...(activeFilters?.jobTitles ?? []).slice(index + 1),
      ];
      updatedQuery.jobTitles = updatedFormValues.jobTitles;
      break;
    case 'jobType':
      updatedFormValues.jobType = '';
      updatedQuery.jobType = updatedFormValues.jobType;
      break;
    case 'tags':
      updatedFormValues.tags = [
        ...(activeFilters?.tags ?? []).slice(0, index),
        ...(activeFilters?.tags ?? []).slice(index + 1),
      ];
      updatedQuery.tags = updatedFormValues.tags;
      break;
    case 'locations':
      updatedFormValues.locations = [
        ...(activeFilters?.locations ?? []).slice(0, index),
        ...(activeFilters?.locations ?? []).slice(index + 1),
      ];
      updatedQuery.candidateAllLocationFields = updatedFormValues?.locations;
      updatedQuery.candidateLocations = updatedFormValues?.locations;
      break;
    case 'experienceComparisonOperator':
      updatedFormValues.experienceComparisonOperator = 'isBetween';
      updatedFormValues.totalYearsExperienceEndRange = 0;
      updatedFormValues.totalYearsExperienceStartRange = 0;

      updatedQuery.experienceComparisonOperator = 'isBetween';
      updatedQuery.totalYearsExperienceStartRange = 0;
      updatedQuery.totalYearsExperienceEndRange = 0;

      break;
    case 'workAuthorization':
      updatedFormValues.workAuthorization = [
        ...(activeFilters?.workAuthorization ?? []).slice(0, index),
        ...(activeFilters?.workAuthorization ?? []).slice(index + 1),
      ];
      updatedQuery.workAuthorizationCategories = updatedFormValues.workAuthorization?.map(
        (workAuth) => workAuth.toLowerCase()
      );
      break;
    case 'currentEmployers':
      updatedFormValues.currentEmployers = [
        ...(activeFilters?.currentEmployers ?? []).slice(0, index),
        ...(activeFilters?.currentEmployers ?? []).slice(index + 1),
      ];
      updatedQuery.currentEmployers = updatedFormValues.currentEmployers;
      break;
    case 'previousEmployers':
      updatedFormValues.previousEmployers = [
        ...(activeFilters?.previousEmployers ?? []).slice(0, index),
        ...(activeFilters?.previousEmployers ?? []).slice(index + 1),
      ];
      updatedQuery.employers = updatedFormValues.previousEmployers;
      break;
    case 'salaryRange':
      updatedFormValues.salaryRange = { currency: null, max: 0, min: 0, unitTime: null };
      updatedQuery.minSalary = 0;
      updatedQuery.maxSalary = 0;
      updatedQuery.salaryCurrency = null;
      updatedQuery.salaryTimeSpan = null;
      break;
    case 'noticePeriod':
      updatedFormValues.noticePeriod = { max: 0, min: 0 };
      updatedQuery.noticePeriodMinDays = 0;
      updatedQuery.noticePeriodMaxDays = 0;
      break;
    case 'languages':
      updatedFormValues.languages = [
        ...(activeFilters?.languages ?? []).slice(0, index),
        ...(activeFilters?.languages ?? []).slice(index + 1),
      ];
      updatedQuery.languages = updatedFormValues.languages;
      break;
    case 'commPreference':
      updatedFormValues.commPreference = [
        ...(activeFilters?.commPreference ?? []).slice(0, index),
        ...(activeFilters?.commPreference ?? []).slice(index + 1),
      ];
      updatedQuery.commPreferencePhone =
        updatedFormValues.commPreference.includes('Phone') ||
        updatedFormValues.commPreference.includes('Both');
      updatedQuery.commPreferenceEmail =
        updatedFormValues.commPreference.includes('Email') ||
        updatedFormValues.commPreference.includes('Both');
      updatedQuery.commType = updatedFormValues.commPreference.includes('Both')
        ? ICommunicationType.All
        : updatedFormValues.commPreference.includes('Email')
        ? ICommunicationType.Email
        : updatedFormValues.commPreference.includes('Phone')
        ? ICommunicationType.Phone
        : undefined;
      break;
    case 'createBWDates':
      updatedQuery.fromDateTime = '';
      updatedQuery.toDateTime = '';
      updatedFormValues.createBWDates = {
        title: '',
        fromDateName: null,
        toDateName: null,
      };
      break;
  }
  return { updatedFormValues, updatedQuery };
};
export const distanceMileOptions: WsSelectObj[] = [
  {
    label: 'Select',
    value: 0,
  },
  {
    label: '20 mi',
    value: 20,
  },
  {
    label: '30 mi',
    value: 30,
  },
  {
    label: '50 mi',
    value: 50,
  },
  {
    label: '100 mi',
    value: 100,
  },
  {
    label: '200 mi',
    value: 200,
  },
  {
    label: '500 mi',
    value: 500,
  },
  {
    label: '1000 mi',
    value: 1000,
  },
];

export const searchScopeOptions: WsSelectObj[] = [
  { label: 'CV', value: 'CV' },
  { label: 'Everywhere', value: 'EVERYTHING' },
];
